import React, { useEffect, useRef, useState } from 'react';
import { identifierLookup, sendCode, passwordLogin, verifyCode, setGuest } from './auth';
import SocialButtons from './SocialButtons';
import { classifyIdentifier } from '../utils/identify';

const EyeIcon = ({ off }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
    <circle cx="12" cy="12" r="3" />
    {off && <line x1="3" y1="3" x2="21" y2="21" />}
  </svg>
);

// Email / GR phone / CY phone — see utils/identify.js, which both this gate and the
// My Account widget share so they can't drift apart.
const classify = classifyIdentifier;

// After a successful login, reload WITHOUT cn_guest (which would otherwise force
// the gate again via forceGuest). Keep cn_preview — during the admin-only phase
// that token is what grants access to the now-authenticated session.
function goToCheckout() {
  const url = new URL(window.location.href);
  url.searchParams.delete('cn_guest');
  window.location.replace(url.toString());
}

// Pre-step: identify the shopper before the 3-step checkout.
//   phase 'id'       → enter email/phone.
//   phase 'auth'     → existing EMAIL account: password OR "send quick code".
//   phase 'code'     → enter the 6-digit code we sent (email/SMS).
//   phase 'password' → privileged account (when passwordless is disabled).
export default function EmailGate({ onIdentified }) {
  const [phase, setPhase] = useState('id');
  const [identifier, setIdentifier] = useState('');
  const [channel, setChannel] = useState('email');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loginUrl, setLoginUrl] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const codeRef = useRef(null);
  const submittedCode = useRef('');

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  useEffect(() => {
    if (phase === 'code' && codeRef.current) codeRef.current.focus();
  }, [phase]);

  // Auto-submit as soon as all 6 digits are in — no need to tap the button.
  useEffect(() => {
    const digits = (code.match(/\d/g) || []).join('');
    if (digits.length === 6 && !loading && submittedCode.current !== digits) {
      doVerify(digits);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const idInfo = classify(identifier);

  // Analytics for the acknowledgement / identify page (between cart and checkout):
  // which path users take (login vs guest), and which auth method (password vs quick
  // code) on which channel (email vs SMS). One event — segment in PostHog by `action`,
  // `channel` ('email'|'phone'), and `method` ('password'|'otp'). Never throws.
  const track = (action, extra = {}) => {
    try {
      if (typeof window !== 'undefined' && window.posthog && typeof window.posthog.capture === 'function') {
        window.posthog.capture('checkout_identify', { action, checkout_flow: 'new', ...extra });
      }
    } catch (e) { /* noop */ }
  };

  // Fires once when the guest/member selection page is actually rendered. Only
  // non-identified users reach this screen (logged-in / returning users skip
  // straight to billing), so this is the true denominator for "how many drop at
  // the guest/member page" — scope identify-page funnels to this event, not to
  // checkout_started (which fires for identified users too).
  useEffect(() => { track('page_shown'); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleContinue(e) {
    e.preventDefault();
    if (!idInfo.valid || loading) return;
    setLoading(true);
    setError('');
    const { ok, data } = await identifierLookup(identifier.trim());
    setLoading(false);

    if (!ok) {
      setError(data.message || 'Κάτι πήγε στραβά. Δοκίμασε ξανά.');
      return;
    }
    const ch = data.channel || idInfo.channel;
    track('identifier_submitted', { channel: ch, account_exists: !!data.exists });
    if (data.use_password) {
      setLoginUrl(data.login_url || '');
      setPhase('password');
      track('login_options_shown', { channel: ch, password_only: true });
      return;
    }
    if (data.exists) {
      setChannel(ch);
      if (data.can_password) {
        // Existing email account → offer password or a quick code.
        setPhase('auth');
        track('login_options_shown', { channel: ch });
      } else {
        // Phone account → code only.
        await requestCode(identifier.trim());
      }
      return;
    }
    await continueAsGuest(identifier.trim(), false);
  }

  async function handlePasswordLogin(e) {
    e.preventDefault();
    if (!password || loading) return;
    setLoading(true);
    setError('');
    track('password_submit', { channel });
    const { ok, data } = await passwordLogin(identifier.trim(), password);
    if (ok && data.logged_in) { track('login_success', { method: 'password', channel }); goToCheckout(); return; }
    setLoading(false);
    if (data.use_password) { setLoginUrl(data.login_url || ''); setPhase('password'); return; }
    setError(data.message || 'Λάθος κωδικός πρόσβασης.');
  }

  // Send (or resend) a one-time code, then move to the code screen.
  async function requestCode(withId) {
    if (loading) return;
    setLoading(true);
    setError('');
    const id = (withId || identifier).trim();
    const { ok, data } = await sendCode(id);
    setLoading(false);
    if (!ok) {
      setError(data.message || 'Δεν ήταν δυνατή η αποστολή του κωδικού.');
      if (data.cooldown) setCooldown(data.cooldown);
      if (data.use_password) { setLoginUrl(data.login_url || ''); setPhase('password'); }
      return;
    }
    const ch = data.channel || classify(id).channel || channel;
    setChannel(ch);
    track('quick_code_requested', { channel: ch }); // channel 'phone' = SMS, 'email' = email
    setCode('');
    submittedCode.current = '';
    setCooldown(data.cooldown || 45);
    setPhase('code');
  }

  function handleVerify(e) {
    e.preventDefault();
    doVerify((code.match(/\d/g) || []).join(''));
  }

  async function doVerify(digits) {
    if (digits.length !== 6 || loading) return;
    submittedCode.current = digits;
    setLoading(true);
    setError('');
    const { ok, data } = await verifyCode(identifier.trim(), digits);
    if (ok && data.logged_in) { track('login_success', { method: 'otp', channel }); goToCheckout(); return; }
    setLoading(false);
    if (data.use_password) { setLoginUrl(data.login_url || ''); setPhase('password'); return; }
    submittedCode.current = ''; // allow a corrected code to auto-submit again
    setError(data.message || 'Λάθος κωδικός.');
  }

  async function handleResend() {
    if (cooldown > 0 || loading) return;
    await requestCode();
  }

  async function continueAsGuest(withId, accountExists) {
    if (loading) return;
    setLoading(true);
    setError('');
    const id = (withId || identifier).trim();
    const ch = classify(id).channel;
    // `explicit` = tapped the "Συνέχεια ως επισκέπτης" button (accountExists undefined);
    // otherwise auto-guest because no account was found for the identifier.
    track('guest_continue', { channel: ch, explicit: accountExists === undefined, account_exists: !!accountExists });
    await setGuest(id);
    setLoading(false);
    onIdentified({ mode: 'guest', identifier: id, channel: ch });
  }

  const sentTo =
    channel === 'phone'
      ? `με SMS στο ${identifier.trim()}`
      : `στο email ${identifier.trim()}`;

  return (
    <div className="cn-gate">
      <div className="cn-gate__card">
        {phase === 'id' && (
          <form onSubmit={handleContinue} noValidate>
            <h1 className="cn-gate__title">Ξεκίνα την παραγγελία σου</h1>
            <p className="cn-gate__sub">Βάλε το email ή το κινητό σου τηλέφωνο για να συνεχίσεις.</p>

            <label className="cn-field">
              <span className="cn-field__label">Email ή κινητό τηλέφωνο</span>
              <input
                type="text"
                inputMode="email"
                autoComplete="email"
                autoFocus
                className="cn-input"
                value={identifier}
                placeholder="you@example.com, 69XXXXXXXX ή 9XXXXXXX"
                onChange={(e) => { setIdentifier(e.target.value); setError(''); }}
              />
            </label>

            {error && <p className="cn-afm-error" role="alert">{error}</p>}

            <button type="submit" className="cn-btn cn-btn--primary" disabled={!idInfo.valid || loading}>
              {loading ? 'Έλεγχος…' : 'Συνέχεια'}
            </button>

            <div className="cn-or"><span>ή</span></div>

            <button type="button" className="cn-btn cn-btn--ghost" disabled={loading} onClick={() => continueAsGuest()}>
              Συνέχεια ως επισκέπτης
            </button>

            <SocialButtons />
          </form>
        )}

        {phase === 'auth' && (
          <form onSubmit={handlePasswordLogin} noValidate>
            <h1 className="cn-gate__title">Έχεις ήδη λογαριασμό</h1>
            <p className="cn-gate__sub">
              Βάλε τον κωδικό πρόσβασής σου για το <strong>{identifier.trim()}</strong>, ή ζήτησε έναν γρήγορο κωδικό μιας χρήσης.
            </p>

            <label className="cn-field">
              <span className="cn-field__label">Κωδικός πρόσβασης</span>
              <div className="cn-pw">
                <input
                  type={showPw ? 'text' : 'password'}
                  autoComplete="current-password"
                  autoFocus
                  className="cn-input cn-input--pw"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(''); }}
                />
                <button
                  type="button"
                  className="cn-pw__toggle"
                  onClick={() => setShowPw((s) => !s)}
                  aria-label={showPw ? 'Απόκρυψη κωδικού' : 'Εμφάνιση κωδικού'}
                  aria-pressed={showPw}
                  tabIndex={-1}
                >
                  <EyeIcon off={showPw} />
                </button>
              </div>
            </label>

            {error && <p className="cn-afm-error" role="alert">{error}</p>}

            <button type="submit" className="cn-btn cn-btn--primary" disabled={!password || loading}>
              {loading ? 'Σύνδεση…' : 'Σύνδεση'}
            </button>

            <div className="cn-or"><span>ή</span></div>

            <button type="button" className="cn-btn cn-btn--ghost" disabled={loading} onClick={() => requestCode()}>
              Στείλε μου γρήγορο κωδικό
            </button>

            <div className="cn-gate__actions">
              <button type="button" className="cn-link" onClick={() => { setPhase('id'); setPassword(''); setError(''); }}>
                ← Αλλαγή
              </button>
            </div>
          </form>
        )}

        {phase === 'code' && (
          <form onSubmit={handleVerify} noValidate>
            <h1 className="cn-gate__title">Βάλε τον κωδικό</h1>
            <p className="cn-gate__sub">
              Σου στείλαμε έναν 6ψήφιο κωδικό {sentTo}. Γράψ' τον παρακάτω.
            </p>

            <label className="cn-field">
              <span className="cn-field__label">Κωδικός</span>
              <input
                ref={codeRef}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                className="cn-input cn-input--code"
                value={code}
                placeholder="______"
                onChange={(e) => { setCode(e.target.value.replace(/\D/g, '')); setError(''); }}
              />
            </label>

            {error && <p className="cn-afm-error" role="alert">{error}</p>}

            <button type="submit" className="cn-btn cn-btn--primary" disabled={(code.match(/\d/g) || []).length !== 6 || loading}>
              {loading ? 'Επαλήθευση…' : 'Επαλήθευση & Σύνδεση'}
            </button>

            <div className="cn-gate__actions">
              <button type="button" className="cn-link" onClick={() => { setPhase('id'); setCode(''); setError(''); }}>
                ← Αλλαγή
              </button>
              <button type="button" className="cn-link" disabled={cooldown > 0 || loading} onClick={handleResend}>
                {cooldown > 0 ? `Νέος κωδικός σε ${cooldown}s` : 'Αποστολή νέου κωδικού'}
              </button>
            </div>
          </form>
        )}

        {phase === 'password' && (
          <div>
            <h1 className="cn-gate__title">Απαιτείται σύνδεση με κωδικό</h1>
            <p className="cn-gate__sub">
              Ο λογαριασμός αυτός έχει αυξημένα δικαιώματα και δεν μπορεί να συνδεθεί με κωδικό email/SMS.
              Παρακαλώ συνδέσου από τη σελίδα σύνδεσης.
            </p>
            {loginUrl && <a className="cn-btn cn-btn--primary" href={loginUrl}>Μετάβαση στη σύνδεση</a>}
            <button type="button" className="cn-link" onClick={() => { setPhase('id'); setError(''); }}>← Πίσω</button>
          </div>
        )}
      </div>
    </div>
  );
}
